import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { enqueue, OutboxStorageError } from './outbox';
import { encodeFormDataInline } from './encode-upload';
import type { OutboxMethod } from './types';

/**
 * Interceptor del modo offline-first. Se aplica al `apiClient` global
 * SOLO cuando `NEXT_PUBLIC_OFFLINE_MODE === '1'`. Cuando está apagado,
 * `installOfflineInterceptors` no se llama desde nadie → cero overhead.
 *
 * Responsabilidades:
 *   1. Inyectar `id` en payload si no viene (UUID v4 generado por
 *      cliente — el backend acepta el campo via `ClientIdDto`).
 *   2. Inyectar header `Idempotency-Key` con el mismo UUID, para que
 *      reintentos del cliente (incluido el drain del outbox) den el
 *      mismo resultado en el servidor.
 *   3. Si estamos offline → encolar en el outbox y devolver una
 *      "respuesta sintética" (200 con `_queued: true`) para que React
 *      Query corra `onSuccess` y la UI se sienta inmediata.
 *
 * Skip explícito vía `config._skipOutbox = true` — el sync engine lo
 * usa para no re-encolar mutaciones que ya está despachando.
 *
 * FormData (uploads): si estamos online se deja pasar tal cual. Si
 * estamos offline, este interceptor llama al helper `encode-upload`
 * (Fase 7b) para convertirla a JSON inline; si el helper rechaza
 * (archivos > 5MB), la petición falla con error claro.
 */

/// Métodos de mutación que pasan por la lógica de outbox.
const MUTATION_METHODS = new Set<OutboxMethod>(['POST', 'PUT', 'PATCH', 'DELETE']);

/// Marker para que el response interceptor reconozca una respuesta
/// "encolada y aún no enviada" y la resuelva como éxito sintético.
const SYNTHETIC_KEY = '__fh_offline_synthetic__';

export interface OfflineAxiosConfig {
  /// `true` desde el sync engine para evitar re-encolar.
  _skipOutbox?: boolean;
}

type ExtConfig = InternalAxiosRequestConfig & OfflineAxiosConfig;

/**
 * Aplica los dos interceptors (request + response) al `apiClient`.
 * Idempotente: registrar dos veces el mismo interceptor sería un bug;
 * el caller debe invocarla una sola vez al arranque.
 */
export function installOfflineInterceptors(client: AxiosInstance): void {
  // Request interceptor: corre ANTES de la red, modifica el config.
  client.interceptors.request.use(async (config) => {
    const ext = config as ExtConfig;
    if (ext._skipOutbox) return config;

    const method = (config.method ?? 'get').toUpperCase() as OutboxMethod | 'GET';
    if (!MUTATION_METHODS.has(method as OutboxMethod)) return config;

    // FormData: si estamos online, se deja pasar tal cual (multipart).
    // Si estamos offline, codificamos los archivos ≤5 MB como base64
    // inline para poder serializarlos en IndexedDB; archivos mayores
    // lanzan `OfflineUploadTooLargeError` desde el helper. El backend
    // los reconstruye en multer via `AcceptInlineBase64Pipe`.
    const isFormData =
      typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isFormData) {
      if (navigator.onLine) {
        return config;
      }
      // Codificar a JSON inline. Si excede el límite, el helper lanza
      // OfflineUploadTooLargeError que sube al caller y se muestra
      // en el toast.
      const encoded = await encodeFormDataInline(config.data as FormData);
      config.data = encoded;
      // Cambiamos el Content-Type para que el backend interprete JSON
      // y nuestro pipe pueda detectar los `_kind: 'inline-upload'`.
      config.headers = config.headers ?? {};
      config.headers['Content-Type'] = 'application/json';
      // Caemos por el camino normal de encolado debajo.
    }

    const url = config.url ?? '';

    // Auth/health → estos NO se encolan ni se tocan: requieren el
    // servidor real (login, refresh de token, healthcheck) y el backend
    // rechaza campos extra. Si estamos offline aquí, dejamos que axios
    // falle con error de red normal y el caller (UI de login) muestra
    // el mensaje al user.
    if (isNeverQueuedPath(url)) {
      return config;
    }

    const body = ensureObject(config.data);

    // `body.id` SOLO se inyecta en POST a un recurso top-level
    // (`POST /api/<resource>`). El backend espera ese campo en
    // `ClientIdDto` y lo respeta como PK. En todos los demás casos
    // (PATCH/PUT/DELETE/actions) el id va EN LA URL, no en body — y
    // tocar el body rompería la validación.
    const isPureCreate = method === 'POST' && isPlainCreatePath(url);
    const idForRecord =
      isPureCreate && typeof body.id === 'string' && body.id.length > 0
        ? body.id
        : crypto.randomUUID();
    if (isPureCreate) {
      body.id = idForRecord;
      config.data = body;
    }

    // `Idempotency-Key` (header) va SIEMPRE: el backend lo dedupe en
    // creates, updates y acciones por igual. No contamina la validación
    // de DTO (es header, no body).
    config.headers = config.headers ?? {};
    if (!config.headers['Idempotency-Key']) {
      config.headers['Idempotency-Key'] = idForRecord;
    }

    // Online → pasa al backend.
    if (navigator.onLine) {
      return config;
    }

    // ─── OFFLINE: encolar TODA mutación (creates, updates, deletes,
    // actions). Cuando vuelva la red, el sync engine las drena en
    // orden FIFO con el mismo Idempotency-Key. ───
    const userId = useAuthStore.getState().user?.id;
    if (!userId) {
      // Sin sesión y offline: no podemos taggear el outbox; deja que
      // axios falle con error de red — la UI redirige a login al
      // recuperar.
      return config;
    }

    const entityType = deriveEntityType(url);
    try {
      await enqueue({
        id: idForRecord,
        userId,
        method: method as OutboxMethod,
        url,
        payload: body,
        entityType,
        entityId: deriveEntityId(url, method as OutboxMethod, body),
      });
    } catch (e) {
      // Si IndexedDB rechaza (quota llena, modo privado…), NO podemos
      // garantizar que la mutación se sincronice más tarde. Propagamos
      // un error claro al caller para que el toast del componente lo
      // muestre. La petición NO se ejecuta — sería peor encolarla
      // "como si nada" y olvidarse de ella.
      if (e instanceof OutboxStorageError) {
        throw e;
      }
      throw new OutboxStorageError(
        'No se pudo guardar la operación offline. Conéctate a internet para intentarlo.',
        e,
      );
    }

    // Lanzamos una excepción especial para que axios la convierta en
    // rejection — el response interceptor (abajo) la atrapa y la
    // resuelve como éxito sintético. Es la forma idiomática de
    // "cortar circuito" en axios sin hacer la petición.
    const synthetic = new Error('queued for offline sync');
    Object.defineProperty(synthetic, SYNTHETIC_KEY, { value: true });
    Object.defineProperty(synthetic, 'config', { value: config });
    Object.defineProperty(synthetic, '__syntheticBody', { value: body });
    Object.defineProperty(synthetic, '__syntheticId', { value: idForRecord });
    throw synthetic;
  });

  // Response interceptor: en rejection, mira si es sintética y la
  // convierte en éxito (status 200, body con `_queued: true`).
  client.interceptors.response.use(
    (response) => response,
    (err: unknown) => {
      if (err && typeof err === 'object' && SYNTHETIC_KEY in err) {
        const e = err as unknown as {
          config: InternalAxiosRequestConfig;
          __syntheticBody: Record<string, unknown>;
          __syntheticId: string;
        };
        const response: AxiosResponse = {
          data: { ...e.__syntheticBody, _queued: true },
          status: 202,
          statusText: 'Accepted (offline queue)',
          headers: { 'x-boosters-ai-offline-queue': 'true' },
          config: e.config,
          // axios espera `request` definido pero acepta `null` para
          // respuestas sintéticas que no han tocado XHR.
          request: null,
        };
        return Promise.resolve(response);
      }
      return Promise.reject(err);
    },
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function ensureObject(data: unknown): Record<string, unknown> {
  if (data == null) return {};
  if (typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  // Body no es objeto (string, array, número…). Lo envolvemos como
  // `{ value }` y añadimos id al wrapper. En la práctica los endpoints
  // del proyecto siempre envían objetos, así que esta rama es defensiva.
  return { value: data };
}

/// Deriva `entityType` del path para invalidación selectiva en React
/// Query post-sync. Ej: `/api/users/abc/roles` → `users`.
function deriveEntityType(url: string): string {
  const m = url.match(/^\/?api\/([a-z0-9-]+)/i);
  return m ? m[1] : 'unknown';
}

/// Decide si el path es una "creación pura" de recurso top-level que
/// soporta `id` opcional en body (vía `ClientIdDto`). Verdadero solo si:
///   - el path es `/api/<resource>` o `/<resource>` SIN segmentos más
///     (sin `:id`, sin `/approve`, sin `/deliver`).
///   - NO es `/auth/*` (el backend rechaza `id` ahí — no extiende DTO).
///
/// Ejemplos:
///   `/api/companies`               → true  (POST create)
///   `/api/companies/uuid`          → false (PATCH update / GET item)
///   `/api/companies/uuid/archive`  → false (acción)
///   `/api/auth/login`              → false
///   `/api/auth/refresh`            → false
export function isPlainCreatePath(url: string): boolean {
  const qless = url.split('?')[0];
  const path = qless.replace(/^\/?(api\/)?/, '/').replace(/^\/+/, '/');
  if (path.startsWith('/auth/') || path === '/auth') return false;
  const segments = path.split('/').filter((s) => s.length > 0);
  return segments.length === 1;
}

/// Paths que NUNCA deben encolarse en el outbox ni recibir `id` /
/// `Idempotency-Key` auto-inyectado. Son endpoints que requieren la
/// red real al servidor: no hay forma sensata de "drenarlos después".
///   - `/auth/*` → login, refresh, register, google, logout. Si te
///     intentas loguear offline, debe fallar inmediatamente — no
///     puedes obtener un access token sin el server.
///   - `/health` → healthcheck, lo usa `useOnlineStatus`. Debe fallar
///     limpio cuando no hay red.
export function isNeverQueuedPath(url: string): boolean {
  const qless = url.split('?')[0];
  const path = qless.replace(/^\/?(api\/)?/, '/').replace(/^\/+/, '/');
  return (
    path.startsWith('/auth/') ||
    path === '/auth' ||
    path === '/health' ||
    path.startsWith('/health/')
  );
}

/// Deriva `entityId` para mutaciones que apuntan a un recurso (PATCH,
/// PUT, DELETE). Acepta UUIDs en cualquier posición del path.
function deriveEntityId(
  url: string,
  method: OutboxMethod,
  body: Record<string, unknown>,
): string | undefined {
  if (method === 'POST') {
    return typeof body.id === 'string' ? body.id : undefined;
  }
  const match = url.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i,
  );
  return match?.[0];
}
